/**
 * * Style utilities for applying consistent styles across components
 */

import { isValidHexColor } from "./color-valid-check";

const normalizeHexToSixDigits = (hex) => {
  if (typeof hex !== "string") {
    return null;
  }

  const sanitizedValue = hex.trim();
  if (!sanitizedValue) {
    return null;
  }

  const withHash = sanitizedValue.startsWith("#")
    ? sanitizedValue
    : `#${sanitizedValue}`;

  if (!isValidHexColor(withHash)) {
    return null;
  }

  if (withHash.length === 4) {
    const [, r, g, b] = withHash;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (withHash.length === 9) {
    return `#${withHash.slice(1, 7)}`.toLowerCase();
  }

  return withHash.toLowerCase();
};

export const hexToRgb = (hex) => {
  const normalizedHex = normalizeHexToSixDigits(hex);

  if (!normalizedHex) {
    return null;
  }

  return {
    r: parseInt(normalizedHex.slice(1, 3), 16),
    g: parseInt(normalizedHex.slice(3, 5), 16),
    b: parseInt(normalizedHex.slice(5, 7), 16),
  };
};

export const hexToRgbaHex = (hex, alpha = 1) => {
  const normalizedHex = hexToRgb(hex);
  if (!normalizedHex) {
    return null;
  }
  const { r, g, b } = normalizedHex;
  const normalizedAlpha = Math.min(Math.max(alpha, 0), 1);
  const a = Math.round(normalizedAlpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}${a}`;
};

/**
 * Relative luminance (WCAG 2.1) for sRGB. Range 0–1.
 * @param {string} hex - 3/6/8 digit hex color
 * @returns {number|null}
 */
export const getRelativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * toLinear(rgb.r) +
    0.7152 * toLinear(rgb.g) +
    0.0722 * toLinear(rgb.b)
  );
};

/**
 * Contrast ratio between two colors (WCAG). L1 = lighter, L2 = darker.
 * @param {number} lum1 - Luminance of lighter color (0–1)
 * @param {number} lum2 - Luminance of darker color (0–1)
 * @returns {number} Ratio in range 1–21
 */
export const getContrastRatio = (lum1, lum2) => {
  const [l1, l2] = lum1 >= lum2 ? [lum1, lum2] : [lum2, lum1];
  return (l1 + 0.05) / (l2 + 0.05);
};

/**
 * Picks black or white for text on the given background (whichever gives higher contrast).
 * @param {string} backgroundHex - Background color in hex
 * @returns {'#000000' | '#ffffff'}
 */
export const getContrastTextColor = (backgroundHex) => {
  const bgLum = getRelativeLuminance(backgroundHex);
  if (bgLum == null) return "#000000";
  const blackLum = getRelativeLuminance("#000000");
  const whiteLum = getRelativeLuminance("#ffffff");
  const ratioOnBlack = getContrastRatio(bgLum, blackLum);
  const ratioOnWhite = getContrastRatio(whiteLum, bgLum);
  return ratioOnBlack >= ratioOnWhite ? "#000000" : "#ffffff";
};

export const tabStylePrefix = "TabStyle";
export const tabStyleClasses = {
  root: `${tabStylePrefix}-root`,
  tabsFlexContainer: `${tabStylePrefix}-tabsFlexContainer`,
  indicator: `${tabStylePrefix}-indicator`,
  tabs: `${tabStylePrefix}-tabs`,
  tab: `${tabStylePrefix}-tab`,
  selectedTab: `${tabStylePrefix}-selectedTab`,
};

export const tabStyleApplier = ({ theme }) => ({
  /**
   * * Root tabs container - match header height (81px)
   */
  flexGrow: 1,
  boxShadow: "none",
  height: "81px",
  alignSelf: "stretch",
  backgroundColor: "transparent",

  /**
   * * Tabs flex container (holds all tabs)
   */
  "& .MuiTabs-flexContainer": {
    height: "81px",
  },

  /**
   * * Tab scroller wrapper
   */
  "& .MuiTabs-scroller": {
    height: "81px",
  },

  /**
   * * Tab indicator (underline)
   */
  "& .MuiTabs-indicator": {
    backgroundColor: "var(--primaryColor)",
  },

  /**
   * * Individual tab buttons
   */
  "& .MuiTab-root": {
    color: "rgba(0, 0, 0, 0.54)",
    textTransform: "none",
    height: "81px",
    minHeight: "81px",
    maxHeight: "81px",
    fontSize: "var(--customFontBodySizeMedium)",

    [theme.breakpoints.down("md")]: {
      height: "81px",
      minHeight: "81px",
      maxHeight: "81px",
    },
  },

  /**
   * * Selected tab
   */
  "& .MuiTab-root.Mui-selected": {
    color: "var(--darkTextColor)",
  },
});
